// One-time "welcome to the team" hand-off between /login and the chat hero.
//
// Joining a team happens on the login screen and then the user is navigated
// straight into the app, so there is no server state that says "this person
// just joined". This module is that signal: /login writes a PENDING record
// after a successful join, the empty-chat hero reads it once, and dismissing
// it stamps a per-team SEEN key so it never comes back (even if a later join
// attempt re-writes the pending record for the same team).
//
// Deliberately localStorage, per device: the card is onboarding for a person
// looking at a screen, not an account-level fact worth a table.

const PENDING_KEY = 'powerai_team_welcome';
const SEEN_PREFIX = 'powerai_team_welcome_seen_';

export interface TeamWelcome {
  teamId: number;
  teamName: string;
}

// Called right after a successful invite join. Silent on any storage failure
// (private mode, quota): a missing welcome card must never break the login.
export function setPendingTeamWelcome(teamId: number, teamName: string): void {
  try {
    if (!Number.isFinite(teamId)) return;
    if (localStorage.getItem(`${SEEN_PREFIX}${teamId}`)) return; // already shown once
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ teamId, teamName: teamName || '' })
    );
  } catch {
    /* storage unavailable - the card simply never shows */
  }
}

// The pending welcome, or null when there is none (or it was already seen).
export function getPendingTeamWelcome(): TeamWelcome | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const teamId = Number(parsed?.teamId);
    if (!Number.isFinite(teamId)) return null;
    if (localStorage.getItem(`${SEEN_PREFIX}${teamId}`)) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return { teamId, teamName: String(parsed?.teamName || '') };
  } catch {
    return null;
  }
}

// The other hand-off: a join that FAILED on the login screen. The only refusal
// a valid invite can still hit is the 409 'team_full' race (the team filled up
// between the mail and the click), and the user lands in the app anyway, so the
// backend's Indonesian sentence has to travel with them. sessionStorage, not
// local: it explains ONE arrival and must not resurface tomorrow.
const JOIN_ERROR_KEY = 'powerai_team_join_error';

export function setTeamJoinError(message: string): void {
  try {
    if (message) sessionStorage.setItem(JOIN_ERROR_KEY, message);
  } catch {
    /* storage unavailable - the notice simply never shows */
  }
}

// Reads and clears in one go: the notice is shown exactly once.
export function takeTeamJoinError(): string {
  try {
    const msg = sessionStorage.getItem(JOIN_ERROR_KEY) || '';
    if (msg) sessionStorage.removeItem(JOIN_ERROR_KEY);
    return msg;
  } catch {
    return '';
  }
}

// Dismissal: drop the pending record and remember that this team's card has
// been seen on this device.
export function dismissTeamWelcome(teamId: number): void {
  try {
    localStorage.removeItem(PENDING_KEY);
    if (Number.isFinite(teamId)) {
      localStorage.setItem(`${SEEN_PREFIX}${teamId}`, '1');
    }
  } catch {
    /* nothing to do */
  }
}
